import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabase.js";

// ─────────────────────────────────────────────────────────────────────────────
// camel ↔ snake helpers
//
// The app's React state uses camelCase (`quotationNumber`); Postgres uses
// snake_case (`quotation_number`). These helpers deep-transform object keys
// without touching values. Arrays are mapped; primitives pass through.
//
// Edge cases:
// - Date strings, UUIDs, currency codes — unaffected (values, not keys).
// - Keys that are already snake_case stay snake_case (idempotent).
// - Children embedded by Supabase eager-load (e.g. `items: quote_items(*)`)
//   come back with snake_case keys too; the same fromRow walks them.
// ─────────────────────────────────────────────────────────────────────────────
const camelToSnake = (s) =>
  s.replace(/[A-Z]/g, (m) => "_" + m.toLowerCase());
const snakeToCamel = (s) =>
  s.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());

// Build a key mapper using an optional explicit override map first, then the
// generic camel↔snake transform as a fallback.
function makeMapper(direction, keyMap) {
  // direction: "out" (camel→snake) or "in" (snake→camel)
  const explicit = {};
  if (keyMap) {
    for (const jsKey of Object.keys(keyMap)) {
      const dbKey = keyMap[jsKey];
      if (direction === "out") explicit[jsKey] = dbKey;       // js → db
      else explicit[dbKey] = jsKey;                            // db → js
    }
  }
  const fallback = direction === "out" ? camelToSnake : snakeToCamel;
  return (k) => (explicit[k] !== undefined ? explicit[k] : fallback(k));
}

function transformKeys(obj, mapKey) {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map((v) => transformKeys(v, mapKey));
  if (typeof obj !== "object") return obj;
  // Skip File / Blob / Date instances — they have no own enumerable keys we want to rewrite.
  if (obj instanceof Date || obj instanceof File || obj instanceof Blob) return obj;
  const out = {};
  for (const k of Object.keys(obj)) {
    out[mapKey(k)] = transformKeys(obj[k], mapKey);
  }
  return out;
}

// Default exports use the generic mapping (no overrides).
export const toRow = (obj, keyMap) => transformKeys(obj, makeMapper("out", keyMap));
export const fromRow = (obj, keyMap) => transformKeys(obj, makeMapper("in", keyMap));

// ─────────────────────────────────────────────────────────────────────────────
// useSupabaseTable
//
// Generic replacement for the old localStorage-backed `useStorage` hook.
//
//   const [rows, save, loaded, remove] = useSupabaseTable("vendors");
//   const [quotes, saveQuotes, qLoaded] = useSupabaseTable("quotes", {
//     select: "*, items:quote_items(*)",  // eager-load children
//     childTable: "quote_items",          // child table name
//     childKey: "items",                  // local JS field that holds children
//     parentFk: "quote_id",               // FK column in child pointing to parent
//     orderBy: "quotation_date",
//     ascending: false,
//     employeeView: "employee_quotes",    // read-side view used when role==='employee'
//     currentRole: role,
//   });
//
// Returns: [rows, save, loaded, remove]
//   rows   — array of records in camelCase shape (matches existing app code)
//   save   — async (next) => upsert all rows; if childKey/childTable set, also
//            reconciles child rows for each parent (insert/update/delete diffs)
//   loaded — boolean, true once initial fetch resolves
//   remove — async (id) => deletes the row by id; cascades delete on children
//            via the child table's FK ON DELETE CASCADE (must be set in schema)
//
// Behavior:
// - Waits for `supabase.auth.getSession()` to succeed before issuing reads —
//   otherwise RLS gives empty rows on first paint.
// - Subscribes to postgres_changes for the parent table; on any change it
//   re-fetches (cheap and avoids tricky local merge logic).
// - Writes go to the *base* table even when reads come from an employee view.
// - All public API stays camelCase. Snake_case is internal.
// ─────────────────────────────────────────────────────────────────────────────
export function useSupabaseTable(tableName, opts = {}) {
  const {
    select = "*",
    orderBy = "created_at",
    ascending = false,
    // Single-child shorthand (most common case — quotes + quote_items, etc.):
    childTable = null,
    childKey = null,      // JS key on the parent that holds the array of children
    parentFk = null,      // FK column on the child table pointing to parent.id
    childKeyMap = null,   // same idea as keyMap, applied to embedded child rows
    // Multi-child support (e.g. purchase_orders with both po_items and vendor_pos):
    // childTables: [{ key, table, fk, keyMap }, ...]
    childTables = null,
    employeeView = null,
    currentRole = null,
    keyMap = null,        // explicit JS↔DB field renames, e.g. { approxWeight: "weight" }
    stripOnWrite = ["createdAt", "updatedAt", "createdOn"],  // DB-managed; never send these
  } = opts;

  // Normalize to an array of child configs (covers both single-child shorthand and explicit childTables).
  const children = childTables
    ? childTables
    : childKey && childTable && parentFk
      ? [{ key: childKey, table: childTable, fk: parentFk, keyMap: childKeyMap }]
      : [];

  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const sessionReadyRef = useRef(false);
  const rowsRef = useRef([]);  // mirrors `rows` for synchronous diffing in save()

  // Choose the read source: employee_quotes view for employees (strips PII),
  // the base table for everyone else. Writes always go to the base table.
  const readSource =
    employeeView && currentRole === "employee" ? employeeView : tableName;

  useEffect(() => {
    let mounted = true;
    let channel = null;

    const fetchRows = async () => {
      const { data, error } = await supabase
        .from(readSource)
        .select(select)
        .order(orderBy, { ascending });
      if (!mounted) return;
      if (error) {
        console.warn(`[useSupabaseTable] ${readSource} fetch failed:`, error.message);
        setRows([]);
      } else {
        // Transform parent and children separately so keyMaps don't clobber each other.
        // Embedded children come back under each child config's `key` (snake_case)
        // from Supabase eager-load (e.g. select("*, items:quote_items(*)")).
        const mapped = (data || []).map((row) => {
          const childPayloads = {};
          let rest = { ...row };
          for (const cfg of children) {
            childPayloads[cfg.key] = rest[cfg.key];
            delete rest[cfg.key];
          }
          const camelParent = fromRow(rest, keyMap);
          for (const cfg of children) {
            const arr = childPayloads[cfg.key];
            camelParent[cfg.key] = Array.isArray(arr)
              ? arr.map((c) => fromRow(c, cfg.keyMap))
              : [];
          }
          return camelParent;
        });
        setRows(mapped);
        rowsRef.current = mapped;
      }
      setLoaded(true);
    };

    (async () => {
      // Gate on auth; RLS reads return empty if we beat the session.
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      sessionReadyRef.current = !!data.session;
      await fetchRows();

      // Realtime — subscribe to the base table (views aren't replicated).
      // Use a unique channel name per effect run: re-using the same name across
      // mounts can land on an already-subscribed channel and Supabase will
      // refuse to add a postgres_changes callback after subscribe().
      const channelName = `${tableName}-realtime-${Math.random().toString(36).slice(2, 10)}`;
      channel = supabase
        .channel(channelName)
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: tableName },
          fetchRows
        )
        .subscribe();
    })();

    // Re-fetch when the auth state changes (login/logout): RLS-visible rows shift.
    const { data: authSub } = supabase.auth.onAuthStateChange(() => {
      if (!mounted) return;
      fetchRows();
    });

    return () => {
      mounted = false;
      if (channel) supabase.removeChannel(channel);
      authSub?.subscription?.unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readSource, tableName, select, orderBy, ascending]);

  // ── save: upsert ONLY changed parents, reconcile their children ──────────
  // For tables with hundreds of rows (e.g. quotes), upserting the entire array
  // on every keystroke would be prohibitively slow. We diff against the
  // last-known state and only send rows whose JSON differs.
  const save = async (nextRows) => {
    if (!Array.isArray(nextRows)) return;

    // Optimistic local update so the UI feels instant.
    setRows(nextRows);
    const prevRows = rowsRef.current;
    rowsRef.current = nextRows;

    // Build an index of previous rows by id for diffing.
    const prevById = new Map();
    for (const r of prevRows) if (r && r.id) prevById.set(r.id, r);

    // Detect deleted parents — present in prev, absent in next.
    const nextIds = new Set(nextRows.map((r) => r && r.id).filter(Boolean));
    const deletedParentIds = prevRows
      .map((r) => r && r.id)
      .filter((id) => id && !nextIds.has(id));

    // Detect changed/new parents: any row whose JSON differs from prev's.
    const changedRows = nextRows.filter((r) => {
      if (!r) return false;
      const prev = r.id ? prevById.get(r.id) : null;
      return !prev || JSON.stringify(prev) !== JSON.stringify(r);
    });

    const stripSet = new Set(stripOnWrite);
    // Convert empty strings to null. Postgres rejects "" for non-text types
    // (date, numeric, uuid). Text columns are unaffected — most app code treats
    // "" and null interchangeably anyway.
    const norm = (v) => (v === "" ? null : v);

    // Strip children + db-managed fields from parent payload.
    const childKeySet = new Set(children.map((c) => c.key));
    const toParentPayload = (r) => {
      const cleaned = {};
      for (const k of Object.keys(r)) {
        if (childKeySet.has(k)) continue;
        if (stripSet.has(k)) continue;
        if (r[k] === undefined) continue;
        cleaned[k] = norm(r[k]);
      }
      return cleaned;
    };

    if (changedRows.length > 0) {
      const parents = changedRows.map(toParentPayload);
      const parentRowsSnake = parents.map((p) => toRow(p, keyMap));
      const { error: parentErr } = await supabase
        .from(tableName)
        .upsert(parentRowsSnake, { onConflict: "id" });
      if (parentErr) {
        console.error(`[useSupabaseTable] ${tableName} parent upsert failed:`, parentErr);
        alert(`Save failed (${tableName}): ${parentErr.message}`);
        return;
      }
    }

    // Delete parents that vanished from the next list.
    if (deletedParentIds.length > 0) {
      const { error: delErr } = await supabase
        .from(tableName)
        .delete()
        .in("id", deletedParentIds);
      if (delErr) console.warn(`[useSupabaseTable] ${tableName} delete failed:`, delErr);
    }

    if (children.length === 0) return;

    // For each configured child table, reconcile only parents whose children changed.
    for (const cfg of children) {
      const parentsToReconcile = changedRows.filter((parent) => {
        const prev = parent.id ? prevById.get(parent.id) : null;
        const prevChildren = prev ? prev[cfg.key] : undefined;
        const nextChildren = parent[cfg.key];
        return JSON.stringify(prevChildren || []) !== JSON.stringify(nextChildren || []);
      });

      for (const parent of parentsToReconcile) {
        const desired = Array.isArray(parent[cfg.key]) ? parent[cfg.key] : [];
        const fkCamel = snakeToCamel(cfg.fk);
        const desiredWithFk = desired.map((child, idx) => {
          const cleaned = {};
          for (const k of Object.keys(child)) {
            if (stripSet.has(k)) continue;
            if (child[k] === undefined) continue;
            cleaned[k] = norm(child[k]);
          }
          cleaned[fkCamel] = parent.id;
          // Always assign position from current array index so that adding,
          // removing, or reordering items keeps positions packed and unique.
          cleaned.position = idx;
          return cleaned;
        });
        const desiredSnake = desiredWithFk.map((c) => toRow(c, cfg.keyMap));

        if (desiredSnake.length > 0) {
          const { error: upErr } = await supabase
            .from(cfg.table)
            .upsert(desiredSnake, { onConflict: "id" });
          if (upErr) {
            console.error(`[useSupabaseTable] ${cfg.table} upsert failed:`, upErr);
            alert(`Save failed (${cfg.table}): ${upErr.message}`);
            return;
          }
        }

        // Delete child rows that used to belong to this parent but no longer do.
        const desiredIds = desiredSnake.map((c) => c.id).filter(Boolean);
        const { data: existing } = await supabase
          .from(cfg.table)
          .select("id")
          .eq(cfg.fk, parent.id);
        const stale = (existing || []).filter((row) => !desiredIds.includes(row.id));
        if (stale.length > 0) {
          const { error: delErr } = await supabase
            .from(cfg.table)
            .delete()
            .in("id", stale.map((r) => r.id));
          if (delErr) console.warn(`[useSupabaseTable] ${cfg.table} stale-delete failed:`, delErr);
        }
      }
    }
  };

  const remove = async (id) => {
    if (!id) return;
    setRows((prev) => prev.filter((r) => r.id !== id));
    const { error } = await supabase.from(tableName).delete().eq("id", id);
    if (error) {
      console.error(`[useSupabaseTable] ${tableName} delete failed:`, error);
      alert(`Delete failed (${tableName}): ${error.message}`);
    }
  };

  return [rows, save, loaded, remove];
}
